import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLoader } from "src/components/page-loader";
import { Button, ErrorBanner, Card, PageTitle } from "src/components";
import Axios from "src/services/api";
import { toast } from "react-hot-toast";

type VerificationState = 'loading' | 'success' | 'failed' | 'error' | 'processing';

const KycComplete: React.FC = () => {
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState<VerificationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleKycCompletion = async () => {
      try {
        // Fetch updated user data including KYC status
        const [userResponse, kycResponse] = await Promise.all([
          Axios.get("/users/me"),
          Axios.get("/users/identity-verification-status")
        ]);
        
        // Update localStorage with the latest user data
        localStorage.setItem("me", JSON.stringify(userResponse.data));
        
        // Check KYC verification status
        if (kycResponse.data.kycVerified) {
          setVerificationState('success');
          toast.success("Identity verification completed successfully!");
          
          // Navigate to payments page after a short delay
          setTimeout(() => {
            navigate("/profile/payments");
          }, 2000);
        } else if (kycResponse.data.sessionStatus === 'processing') {
          // Verification is still processing
          setVerificationState('processing');
          setErrorMessage("Your identity verification is being reviewed. This usually takes a few minutes.");
        } else {
          // Verification failed
          setVerificationState('failed');
          setErrorMessage(
            kycResponse.data.sessionStatus === 'requires_input' 
              ? "Additional information is required to complete your verification."
              : "Identity verification was not successful. Please try again."
          );
        }
        
      } catch (error: any) {
        console.error("Error fetching user data after KYC completion:", error);
        setVerificationState('error');
        setErrorMessage("Error checking your verification status. Please try again.");
      }
    };

    // Add a small delay to ensure the backend has processed the verification
    const timer = setTimeout(() => {
      handleKycCompletion();
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleRetryVerification = () => {
    navigate("/profile/payments");
  };

  if (verificationState === 'loading') {
    return (
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center", 
        minHeight: "60vh",
        gap: "16px"
      }}>
        <PageLoader />
        <p style={{ 
          color: "#666", 
          textAlign: "center",
          fontSize: "16px",
          marginTop: "16px"
        }}>
          Processing your identity verification...
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
      <PageTitle>Identity Verification</PageTitle>
      
      <Card>
        <div style={{ padding: "24px" }}>
          {verificationState === 'success' && (
            <>
              <ErrorBanner
                message="Identity verification completed successfully! You can now connect your bank account and start receiving payments."
                type="success"
              />
              <p style={{ 
                color: "#666", 
                textAlign: "center",
                fontSize: "14px",
                marginTop: "16px"
              }}>
                Redirecting you to payments page...
              </p>
            </>
          )}

          {verificationState === 'processing' && (
            <>
              <ErrorBanner
                message={errorMessage}
                type="info"
              />
              <div style={{ marginTop: "16px" }}>
                <p style={{ 
                  color: "#666", 
                  fontSize: "14px",
                  lineHeight: "1.5",
                  marginBottom: "12px"
                }}>
                  Please wait while we review your submitted documents. You will receive an email notification once the verification is complete.
                </p>
                <p style={{ 
                  color: "#666", 
                  fontSize: "14px",
                  lineHeight: "1.5"
                }}>
                  In the meantime, you can continue setting up your account. We'll notify you as soon as your identity is verified.
                </p>
              </div>
              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <Button
                  variant="secondary"
                  text="Go to Payments"
                  onClick={handleRetryVerification}
                />
              </div>
            </>
          )}

          {(verificationState === 'failed' || verificationState === 'error') && (
            <>
              <ErrorBanner 
                message={errorMessage}
                type="error"
              />
              <div style={{ marginTop: "16px" }}>
                <p style={{ 
                  color: "#666", 
                  fontSize: "14px",
                  lineHeight: "1.5",
                  marginBottom: "12px"
                }}>
                  {verificationState === 'failed' 
                    ? "Don't worry, you can try the verification process again. Make sure you have a valid government-issued ID ready."
                    : "There was a technical issue checking your verification status. Please try again."
                  }
                </p>
              </div>
              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <Button
                  variant="primary"
                  text="Try Again"
                  onClick={handleRetryVerification}
                />
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default KycComplete; 